import {
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { BaseModel } from '@cotera/client/v0/actions/framework/model.base';
import type { PromptInputMessage } from '@cotera/client/v0/components/ai-elements/prompt-input';
import { getMessageTextFromPayload } from '../utils/prompt-utils';
import type { ChatStatus } from './coco-chat.model';

/** A message the user submitted while a run was in flight, waiting to be sent. */
export interface QueuedMessage {
  /** Stable id for React keys and targeted removal. */
  readonly id: string;
  /** The original composer message, sent verbatim when the queue drains. */
  readonly message: PromptInputMessage;
  /** Plain-text/markdown preview for the pending-messages UI (may be empty). */
  readonly preview: string;
  /** Number of attachments carried by the message. */
  readonly attachmentCount: number;
}

/** How a drained message is actually sent (the chat submit path). */
export type DrainHandler = (message: PromptInputMessage) => Promise<void>;

// Monotonic so two messages queued within the same millisecond keep distinct ids.
let queuedMessageCounter = 0;

function derivePreview(message: PromptInputMessage): string {
  return message.type === 'text'
    ? getMessageTextFromPayload({ text: message.text })
    : getMessageTextFromPayload({ editorState: message.editorState });
}

/**
 * Holds messages the user submits while a Coco run is still in flight, and
 * drains them one at a time as the conversation settles.
 *
 * Submitting during a run used to cancel it and immediately resend; instead we
 * enqueue here. The model subscribes to the chat status and, whenever a run
 * returns to `ready` (and is not locally cancelled), sends the next queued
 * message through the registered {@link DrainHandler} — the normal submit path.
 * Only one send is in flight at a time; later messages drain as subsequent runs
 * finish. The "Send now" affordance in the pending-messages UI opts a single
 * item back into the cancel-and-resend behaviour.
 *
 * The drain decision is deferred to a microtask ({@link scheduleDrain}) so the
 * two synchronous writes a stop performs (`status := ready` then
 * `cancelled := true`) are read together, not mid-flight — otherwise a "Send
 * now"/Stop would be seen as a normal settle and double-send.
 *
 * If a send fails, the message goes back to the front of the queue (never
 * dropped) and auto-draining pauses, so a persistently failing send is not
 * retried in a tight loop; the queued message stays visible with its "Send now"
 * affordance, and the next genuine run clears the pause.
 *
 * Created per conversation via ModelScopeFactory, mirroring HijackerQueueModel,
 * so switching conversations starts with an empty queue.
 */
export class MessageQueueModel extends BaseModel {
  private readonly queue: Watchable<QueuedMessage[]> = Watchable.fromValue<
    QueuedMessage[]
  >([]);

  readonly queueWatchable: ReadonlyWatchable<QueuedMessage[]> = this.queue;

  private drainHandler: DrainHandler | undefined;
  private draining = false;
  private pausedAfterFailure = false;
  private drainScheduled = false;
  private readonly unsubscribe: () => void;

  constructor(
    conversationId: string,
    private readonly statusWatchable: ReadonlyWatchable<ChatStatus>,
    private readonly cancelledWatchable: ReadonlyWatchable<boolean>
  ) {
    super(`message-queue-${conversationId}`);
    // Draining is driven entirely off status/cancelled transitions; a message
    // can only be queued while a run is in flight, so the queue itself never
    // needs to trigger a drain. The failure pause is cleared here, synchronously
    // per transition, rather than in drainNext: drainNext is coalesced and only
    // ever sees the final status of a tick, so it would miss the intermediate
    // 'streaming'/'submitted' that marks a genuine new run.
    const onStatusChange = () => {
      const status = this.statusWatchable.snapshot();
      if (status === 'streaming' || status === 'submitted') {
        this.pausedAfterFailure = false;
      }
      this.scheduleDrain();
    };
    const unsubStatus = this.statusWatchable.subscribe(onStatusChange);
    const unsubCancelled = this.cancelledWatchable.subscribe(() =>
      this.scheduleDrain()
    );
    this.unsubscribe = () => {
      unsubStatus();
      unsubCancelled();
    };
  }

  dispose(): void {
    this.unsubscribe();
  }

  /**
   * Register how a drained message is sent (the chat submit path). Called by the
   * view; safe to call repeatedly as the handler identity changes across renders.
   */
  setDrainHandler(handler: DrainHandler): void {
    this.drainHandler = handler;
    this.scheduleDrain();
  }

  /** Append a message to the end of the queue and return the created item. */
  enqueue(message: PromptInputMessage): QueuedMessage {
    const item: QueuedMessage = {
      id: `queued-${(queuedMessageCounter += 1)}`,
      message,
      preview: derivePreview(message),
      attachmentCount: message.files?.length ?? 0,
    };
    this.queue.set([...this.queue.snapshot(), item]);
    return item;
  }

  /** Remove and return the first queued message, or undefined when empty. */
  dequeue(): QueuedMessage | undefined {
    const [first, ...rest] = this.queue.snapshot();
    if (first === undefined) {
      return undefined;
    }
    this.queue.set(rest);
    return first;
  }

  /**
   * Put a previously dequeued message back at the front of the queue, keeping
   * its original id so the pending-messages UI preserves its React key. Used
   * when draining a message fails: the send is retried from the front rather
   * than silently dropping the user's message.
   */
  requeueFront(item: QueuedMessage): void {
    this.queue.set([item, ...this.queue.snapshot()]);
  }

  /** Drop a single queued message by id (used by the UI remove/submit-now). */
  remove(id: string): void {
    const next = this.queue.snapshot().filter((item) => item.id !== id);
    if (next.length !== this.queue.snapshot().length) {
      this.queue.set(next);
    }
  }

  clear(): void {
    if (this.queue.snapshot().length > 0) {
      this.queue.set([]);
    }
  }

  get length(): number {
    return this.queue.snapshot().length;
  }

  // Coalesce the several synchronous watchable writes that can happen in one tick
  // (a stop writes `status` then `cancelled`) into a single drain decision read
  // from the settled snapshots.
  private scheduleDrain(): void {
    if (this.drainScheduled) {
      return;
    }
    this.drainScheduled = true;
    queueMicrotask(() => {
      this.drainScheduled = false;
      this.drainNext();
    });
  }

  private drainNext(): void {
    if (
      this.drainHandler === undefined ||
      this.statusWatchable.snapshot() !== 'ready' ||
      this.cancelledWatchable.snapshot() ||
      this.draining ||
      this.pausedAfterFailure
    ) {
      return;
    }
    const next = this.dequeue();
    if (next === undefined) {
      return;
    }
    this.draining = true;
    void this.drainHandler(next.message)
      .catch(() => {
        // Delivery failed: put the message back at the front so it is not lost
        // and pause auto-draining until a genuine run resumes it. The handler
        // surfaces its own error; the user can retry via the row's "Send now".
        this.requeueFront(next);
        this.pausedAfterFailure = true;
      })
      .finally(() => {
        this.draining = false;
      });
  }
}
